import { booleanAttribute, computed, Directive, input } from '@angular/core';
import {
  BADGE_SIZES,
  BADGE_TONES,
  BADGE_VARIANTS,
  type BadgeSize,
  type BadgeTone,
  type BadgeVariant,
} from '@groove/core';

// The axis vocabularies are owned by @groove/core (framework-neutral, so the
// coverage engine can read them without pulling in @angular/core). Re-exported
// here so consumers can keep importing them from the directive.
export {
  BADGE_SIZES,
  BADGE_TONES,
  BADGE_VARIANTS,
  type BadgeSize,
  type BadgeTone,
  type BadgeVariant,
};

/**
 * `.groove-badge` — element tier.
 *
 * The variant vocabulary is deliberately the same as the action/button one
 * (solid / outline / ghost) so the two read as one system. The "subtle pill"
 * look is `variant="solid"` + `tone="muted"`, not a variant of its own.
 *
 * Default values (solid / sm / default) are omitted from the DOM to keep
 * attributes clean (omit-default-attribute pattern).
 * Booleans reflect as `data-X="true"`; omit when false.
 */
@Directive({
  selector: '[grooveBadge]',
  standalone: true,
  host: {
    class: 'groove-badge',
    '[attr.data-variant]': 'variantAttr()',
    '[attr.data-size]': 'sizeAttr()',
    '[attr.data-tone]': 'toneAttr()',
    '[attr.data-mono]': 'mono() ? "true" : null',
  },
})
export class GrooveBadgeDirective {
  readonly variant = input<BadgeVariant>('solid');
  readonly size = input<BadgeSize>('sm');
  readonly tone = input<BadgeTone>('default');
  readonly mono = input(false, { transform: booleanAttribute });

  // Omit default attrs to keep the DOM clean.
  protected readonly variantAttr = computed(() =>
    this.variant() === 'solid' ? null : this.variant(),
  );
  protected readonly sizeAttr = computed(() =>
    this.size() === 'sm' ? null : this.size(),
  );
  protected readonly toneAttr = computed(() =>
    this.tone() === 'default' ? null : this.tone(),
  );
}
